/**
 * Per-tour gallery-image mutations — upload + delete. Reads continue to
 * use the public gallery.json fetch inside MediaGallery; this module
 * only covers the write paths the Developer Mode panel exposes.
 *
 * Backed by the existing tour-gallery-api gen2 GCFs (anonymous CORS).
 */

export interface GalleryImageRecord {
  id: string;
  url: string;
  uploadedAt?: string;
  caption?: string;
  tags?: string[];
}

function baseUrl(): string {
  const url = process.env.REACT_APP_GALLERY_API_BASE_URL;
  if (!url) {
    throw new Error('REACT_APP_GALLERY_API_BASE_URL is not configured');
  }
  return url.replace(/\/$/, '');
}

function stripPrefix(tourId: string): string {
  return tourId.startsWith('tours/') ? tourId.slice(6) : tourId;
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export const MAX_GALLERY_UPLOAD_BYTES = 10 * 1024 * 1024;

export async function uploadGalleryImage(
  tourId: string,
  file: File,
  options: { caption?: string; tags?: string[] } = {},
): Promise<GalleryImageRecord> {
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }
  if (file.size > MAX_GALLERY_UPLOAD_BYTES) {
    throw new Error(
      `Image must be ${MAX_GALLERY_UPLOAD_BYTES / 1024 / 1024} MB or smaller`,
    );
  }
  const imageData = await readFileAsDataUrl(file);
  const res = await fetch(`${baseUrl()}/tourGallerySave`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tourId: stripPrefix(tourId),
      imageData,
      caption: options.caption,
      tags: options.tags,
    }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      detail = (await res.json())?.error || '';
    } catch {
      /* ignore */
    }
    throw new Error(detail || `gallery POST returned ${res.status}`);
  }
  const body = (await res.json()) as { image: GalleryImageRecord };
  const image = body.image;
  // The deployed tourGallerySave predates tag support and silently drops
  // `tags`, which leaves the photo outside every variant tab. Apply them
  // with tourGalleryUpdate when the save response comes back untagged.
  if (options.tags?.length && !image.tags?.length) {
    return updateGalleryImage(tourId, image.id, { tags: options.tags });
  }
  return image;
}

export async function updateGalleryImage(
  tourId: string,
  imageId: string,
  patch: { caption?: string; tags?: string[] },
): Promise<GalleryImageRecord> {
  const res = await fetch(`${baseUrl()}/tourGalleryUpdate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tourId: stripPrefix(tourId), imageId, ...patch }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      detail = (await res.json())?.error || '';
    } catch {
      /* ignore */
    }
    throw new Error(detail || `gallery UPDATE returned ${res.status}`);
  }
  const body = (await res.json()) as { image: GalleryImageRecord };
  return body.image;
}

export async function deleteGalleryImage(
  tourId: string,
  imageId: string,
): Promise<void> {
  const res = await fetch(`${baseUrl()}/tourGalleryDelete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tourId: stripPrefix(tourId), imageId }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      detail = (await res.json())?.error || '';
    } catch {
      /* ignore */
    }
    throw new Error(detail || `gallery DELETE returned ${res.status}`);
  }
}
